import {
  Component,
  OnInit,
  Injectable,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormArray,
  Validators,
  Form,
} from '@angular/forms';
import { IOption } from 'src/app/interfaces/option';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { FileItem, FileLikeObject, FileUploader } from 'ng2-file-upload';

import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { faImage } from '@fortawesome/free-solid-svg-icons';

import Swal from 'sweetalert2/dist/sweetalert2.js';
import { CategoryService } from 'src/app/services/category.service';
import { ICategories } from 'src/app/interfaces/categories';
import { ICategory } from 'src/app/interfaces/category';
import { ISubcategory } from 'src/app/interfaces/subcategory';
import { AppSettings } from 'src/app/appSettings';

/**
 * Node for to-do item
 */
export class TodoItemNode {
  children: TodoItemNode[];
  item: string;
  id: number;
  level: number;
  icon: string;
  changed: boolean;
  owner: boolean;
}

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
  id: number;
  item: string;
  icon: string;
  level: number;
  expandable: boolean;
  changed: boolean;
  owner: boolean;
}

/**
 * The Json object for to-do list data.
 */
// const TREE_DATA = {
//   Groceries: {
//     'Almond Meal flour': null,
//     'Organic eggs': null,
//     'Protein Powder': null,
//     Fruits: {
//       Apple: null,
//       Berries: ['Blueberry', 'Raspberry'],
//       Orange: null,
//     },
//   },
//   Reminders: [
//     'Cook dinner',
//     'Read the Material Design spec',
//     'Upgrade Application to Angular',
//   ],
// };

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<TodoItemNode[]>([]);

  get data(): TodoItemNode[] {
    return this.dataChange.value;
  }

  constructor() {
    // this.initialize();
  }

  initialize(list: ICategory[]) {
    // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
    //     file node as children.
    const data = this.buildFileTree(list, 0);

    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TodoItemNode`.
   */
  buildFileTree(
    obj: ICategory[] | ISubcategory[],
    level: number
  ): TodoItemNode[] {
    return Object.values(obj).reduce<TodoItemNode[]>(
      (accumulator, category) => {
        const value = category['subcategorias'];
        const node = new TodoItemNode();
        node.item =
          level == 0 ? category.ds_categoria : category.ds_subcategoria;
        node.id = level == 0 ? category.idcategoria : category.idsubcategoria;
        node.icon = level == 0 ? category.icon : '';
        node.level = level;
        node.changed = false;
        node.owner = category.owner;

        if (value != null) {
          if (typeof value === 'object') {
            node.children = this.buildFileTree(value, level + 1);
          }
        }

        return accumulator.concat(node);
      },
      []
    );
  }

  /** Add an item to to-do list */
  insertItem(parent: TodoItemNode, node: TodoItemNode): TodoItemNode {
    if (!parent.children) {
      parent.children = [];
    }
    const newItem = {
      item: node.item,
      id: node.id,
      changed: true,
    } as TodoItemNode;
    parent.children.push(newItem);
    this.dataChange.next(this.data);
    return newItem;
  }

  insertItemAbove(node: TodoItemNode, move: TodoItemNode): TodoItemNode {
    const parentNode = this.getParentFromNodes(node);
    const newItem = {
      item: move.item,
      id: move.id,
      changed: true,
    } as TodoItemNode;
    if (parentNode != null) {
      parentNode.children.splice(parentNode.children.indexOf(node), 0, newItem);
    } else {
      this.data.splice(this.data.indexOf(node), 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  insertRootItem(node: TodoItemNode): TodoItemNode {
    const newItem = {
      item: node.item,
      id: node.id,
      icon: node.icon,
      children: node.children,
      changed: true,
    } as TodoItemNode;

    this.data.splice(this.data.length, 0, newItem);

    this.dataChange.next(this.data);
    return newItem;
  }

  insertItemBelow(node: TodoItemNode, move: TodoItemNode): TodoItemNode {
    const parentNode = this.getParentFromNodes(node);
    const newItem = {
      item: move.item,
      id: move.id,
      changed: true,
    } as TodoItemNode;
    if (parentNode != null) {
      parentNode.children.splice(
        parentNode.children.indexOf(node) + 1,
        0,
        newItem
      );
    } else {
      this.data.splice(this.data.indexOf(node) + 1, 0, newItem);
    }
    this.dataChange.next(this.data);
    return newItem;
  }

  getParentFromNodes(node: TodoItemNode): TodoItemNode {
    for (let i = 0; i < this.data.length; ++i) {
      const currentRoot = this.data[i];
      const parent = this.getParent(currentRoot, node);
      if (parent != null) {
        return parent;
      }
    }
    return null;
  }

  getParent(currentRoot: TodoItemNode, node: TodoItemNode): TodoItemNode {
    if (currentRoot.children && currentRoot.children.length > 0) {
      for (let i = 0; i < currentRoot.children.length; ++i) {
        const child = currentRoot.children[i];
        if (child === node) {
          return currentRoot;
        } else if (child.children && child.children.length > 0) {
          const parent = this.getParent(child, node);
          if (parent != null) {
            return parent;
          }
        }
      }
    }
    return null;
  }

  updateItem(node: TodoItemNode, name: string) {
    node.item = name;
    node.changed = true;
    this.dataChange.next(this.data);
  }

  deleteItem(node: TodoItemNode) {
    this.deleteNode(this.data, node);
    this.dataChange.next(this.data);
  }

  copyPasteItem(from: TodoItemNode, to: TodoItemNode): TodoItemNode {
    const newItem = this.insertItem(to, from);
    if (from.children) {
      from.children.forEach((child) => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemAbove(from: TodoItemNode, to: TodoItemNode): TodoItemNode {
    const newItem = this.insertItemAbove(to, from);
    if (from.children) {
      from.children.forEach((child) => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  copyPasteItemBelow(from: TodoItemNode, to: TodoItemNode): TodoItemNode {
    const newItem = this.insertItemBelow(to, from);
    if (from.children) {
      from.children.forEach((child) => {
        this.copyPasteItem(child, newItem);
      });
    }
    return newItem;
  }

  deleteNode(nodes: TodoItemNode[], nodeToDelete: TodoItemNode) {
    const index = nodes.indexOf(nodeToDelete, 0);
    if (index > -1) {
      nodes.splice(index, 1);
    } else {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          this.deleteNode(node.children, nodeToDelete);
        }
      });
    }
  }
}

@Component({
  selector: 'app-categories',
  templateUrl: './categories.component.html',
  styleUrls: ['./categories.component.scss'],
  host: { class: 'wrapper-settings' },
  providers: [ChecklistDatabase],
})
export class CategoriesComponent implements OnInit {
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, TodoItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TodoItemNode, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

  dataSource: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(
    true /* multiple */
  );

  /* Drag and drop */
  dragNode: any;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number;
  dragNodeExpandOverArea: number;
  @ViewChild('emptyItem') emptyItem: ElementRef;

  public loading: boolean = false;
  public showModal: boolean = false;
  public openModal: boolean = false;
  public modalType: string = '';
  public changed: boolean;
  public settingsFrm: FormGroup;

  public nodeSelected!: TodoItemFlatNode;
  public categorySelected: string = '';

  public categoryDeleted: any[] = [];

  // padrao
  public title!: FormControl;

  public faImage = faImage;

  public imageIcon: string = '';
  public uploader!: FileUploader;
  public hasBaseDropZoneOver: boolean = false;

  constructor(
    private database: ChecklistDatabase,
    private _categoryService: CategoryService
  ) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<TodoItemFlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );

    database.dataChange.subscribe((data) => {
      this.dataSource.data = [];
      this.dataSource.data = data;
      if (this.loading) this.loading = false;
    });

    this.getCategoriesTree();

    /**
     *
     * envio do logo do estabelecimento
     *
     */

    this.uploader = new FileUploader({
      url: AppSettings.API_ENDPOINT,
      allowedFileType: ['image'],
      allowedMimeType: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
      maxFileSize: 1048576 * 2,
      filters: [
        {
          name: 'imageFilter',
          fn: function (item: FileLikeObject | undefined, options) {
            if (item === undefined) return false;
            var type =
              '|' + item.type.slice(item.type.lastIndexOf('/') + 1) + '|';
            return '|jpg|png|jpeg|gif|webp|'.indexOf(type) !== -1;
          },
        },
      ],
    });

    this.uploader.onAfterAddingFile = (f) => {
      f.withCredentials = false;

      if (this.uploader.queue.length > 1) {
        this.uploader.removeFromQueue(this.uploader.queue[0]);
      }

      this.imageIcon = '';
    };

    this.uploader.onCompleteItem = (
      item: any,
      response: any,
      status: number,
      headers: any
    ) => {
      console.log(response);
      if (status == 200) {
        var responsePath = JSON.parse(response);
        if (responsePath['success']) {
          this.imageIcon = responsePath['icon'];
          this.nodeSelected.icon = responsePath['icon'];
        }
      }
    };

    this.uploader.onCompleteAll = () => {
      this.uploader.clearQueue();
      this.saveComplete('A imagem do ícone foi salva!');
    };

    this.uploader.onErrorItem = (
      item: FileItem,
      response: any,
      status: any,
      headers: any
    ) => {
      item.remove();

      Swal.fire(
        'Ops!',
        'Ocorreu um erro ao processar a imagem, verifique se a imagem está correta! Se é um arquivo válido de imagem (png, jpg, webp, gif) com no máximo 2 MB de tamanho',
        'error'
      );
    };

    this.uploader.onWhenAddingFileFailed = function (
      item: FileLikeObject,
      filter: any,
      options: any
    ) {
      Swal.fire(
        'Ops!',
        'O arquivo selecionado não é válido, verifique se é um arquivo de imagem (png, jpg, webp, gif) com no máximo 2 MB de tamanho!',
        'error'
      );
    };
  }

  getCategoriesTree() {
    this.categoryDeleted = [];
    this.loading = true;
    this._categoryService.getTree().subscribe((res) => {
      this.database.initialize(res.categorias);
    });
  }

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: TodoItemNode): TodoItemNode[] => node.children;

  hasChild = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TodoItemFlatNode) =>
    _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TodoItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.item
        ? existingNode
        : new TodoItemFlatNode();
    flatNode.item = node.item;
    flatNode.id = node.id;
    flatNode.icon = node.icon;
    flatNode.level = level;
    flatNode.changed = node.changed;
    flatNode.expandable = node.children && node.children.length > 0;
    flatNode.owner = node.owner;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /** Whether all the descendants of the node are selected */
  descendantsAllSelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) =>
      this.checklistSelection.isSelected(child)
    );
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);
  }

  /** Select the category so we can insert the new item. */
  addNewItem(node: TodoItemFlatNode) {
    this.onOpenModal('new');

    this.nodeSelected = node;
    this.categorySelected = '';
  }

  /** Save the node to database */
  saveNode(node: TodoItemFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this.database.updateItem(nestedNode, itemValue);
  }

  /**  */
  newCat() {
    this.onOpenModal('new-cat');

    this.categorySelected = '';
  }

  /**  */
  addIcon(node: TodoItemFlatNode) {
    this.nodeSelected = node;
    this.imageIcon = node.icon;
    this.onOpenModal('add-icon');
  }

  editText(node: TodoItemFlatNode) {
    this.nodeSelected = node;
    this.categorySelected = node.item;
    this.onOpenModal('edit');
  }

  handleDragStart(event, node) {
    // Required by Firefox (https://stackoverflow.com/questions/19055264/why-doesnt-html5-drag-and-drop-work-in-firefox)
    event.dataTransfer.setData('foo', 'bar');
    //event.dataTransfer.setDragImage(this.emptyItem.nativeElement, 0, 0);
    this.dragNode = node;
    this.treeControl.collapse(node);
  }

  handleDragOver(event, node) {
    event.preventDefault();
    // Handle node expand
    if (this.dragNodeExpandOverNode && node === this.dragNodeExpandOverNode) {
      if (
        Date.now() - this.dragNodeExpandOverTime >
        this.dragNodeExpandOverWaitTimeMs
      ) {
        if (!this.treeControl.isExpanded(node)) {
          this.treeControl.expand(node);
          //this.cd.detectChanges();
        }
      }
    } else {
      this.dragNodeExpandOverNode = node;
      this.dragNodeExpandOverTime = new Date().getTime();
    }

    // Handle drag area
    // const percentageY = event.offsetY / event.target.clientHeight;
    // if (0 <= percentageY && percentageY <= 0.25) {
    //   this.dragNodeExpandOverArea = 1;
    // } else if (1 >= percentageY && percentageY >= 0.75) {
    //   this.dragNodeExpandOverArea = -1;
    // } else {
    //   this.dragNodeExpandOverArea = 0;
    // }
  }

  handleDrop(event, node) {
    console.log(node, this.dragNode);
    if (node !== this.dragNode) {
      let newItem: TodoItemNode;
      // if (this.dragNodeExpandOverArea === 1) {
      //   newItem = this.database.copyPasteItemAbove(
      //     this.flatNodeMap.get(this.dragNode),
      //     this.flatNodeMap.get(node)
      //   );
      // } else if (this.dragNodeExpandOverArea === -1) {
      //   newItem = this.database.copyPasteItemBelow(
      //     this.flatNodeMap.get(this.dragNode),
      //     this.flatNodeMap.get(node)
      //   );
      // } else {
      newItem = this.database.copyPasteItem(
        this.flatNodeMap.get(this.dragNode),
        this.flatNodeMap.get(node)
      );
      // }
      this.database.deleteItem(this.flatNodeMap.get(this.dragNode));
      this.treeControl.expandDescendants(this.nestedNodeMap.get(newItem));
    }
    this.handleDragEnd(event);
  }

  handleDragEnd(event) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
    this.dragNodeExpandOverArea = NaN;
    event.preventDefault();
    this.changed = true;
  }

  getStyle(node: TodoItemFlatNode) {
    let nameClass = [];
    if (this.dragNode === node) {
      nameClass.push('drag-start');
    } else if (this.dragNodeExpandOverNode === node) {
      // switch (this.dragNodeExpandOverArea) {
      //   case 1:
      //     nameClass.push('drop-above');
      //     break;
      //   case -1:
      //     nameClass.push('drop-below');
      //     break;
      //   default:
      nameClass.push('drop-center');
      //     break;
      // }
    }

    if (node.level == 0) nameClass.push('mat-tree-node-root');

    // if (this.treeControl.isExpanded(node)) nameClass.push('border-none');

    return nameClass;
  }

  deleteCheck() {
    Swal.fire({
      title: 'Verificando se está em uso...',
      icon: 'info',
      showCancelButton: false,
      allowOutsideClick: false,
      showConfirmButton: false,
    });
  }

  deleteCheckError() {
    Swal.fire(
      'Ops!',
      'Este item já está em uso e não é possível remover.',
      'error'
    );
  }

  deleteItem(node: TodoItemFlatNode) {
    Swal.fire({
      title: 'Você deseja realmente remover?',
      text: 'é preciso salvar para concluir a exclusão',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, desejo!',
    }).then((result) => {
      if (result.value) {
        if (node.id > 0) {
          this.deleteCheck();

          let checkObserver = null;

          if (node.level == 0) {
            checkObserver = this._categoryService.checkCategoryProduct(node.id);
          } else {
            checkObserver = this._categoryService.checkSubcategoryProduct(
              node.id
            );
          }

          if (checkObserver)
            checkObserver.subscribe((res) => {
              if (res['success'] && !res['has']) {
                this.categoryDeleted.push({
                  id: node.id,
                  t: node.level == 0 ? 'C' : 'S',
                });
                this.database.deleteItem(this.flatNodeMap.get(node));
                this.changed = true;
                Swal.close();
              } else {
                this.deleteCheckError();
              }
            });
        } else {
          this.database.deleteItem(this.flatNodeMap.get(node));
        }
      }
    });
  }

  /**
   *
   */

  ngOnInit(): void {
    this.createFormControls();
    this.createForm();
  }

  createFormControls() {
    // padrao
    this.title = new FormControl('');
  }

  createForm() {
    this.settingsFrm = new FormGroup({
      title: this.title,
    });
  }

  onChangeDetail() {
    this.changed = true;
  }

  onCancel() {
    Swal.fire({
      title: 'Você deseja cancelar?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, desejo!',
    }).then((result) => {
      if (result.value) {
        this.loading = true;
        this.changed = false;
        this.getCategoriesTree();
      }
    });
  }

  onOpenModal(tipo) {
    this.showModal = true;
    this.modalType = tipo;
    setTimeout(() => {
      this.openModal = true;
    }, 200);
  }

  okModal() {
    let parentNode = null;
    switch (this.modalType) {
      case 'new':
        if (this.categorySelected != '') {
          parentNode = this.flatNodeMap.get(this.nodeSelected);
          this.database.insertItem(parentNode, {
            item: this.categorySelected,
            id: null,
            children: [],
            icon: '',
            level: parentNode.level + 1,
            changed: true,
            owner: true,
          });
          this.treeControl.expand(this.nodeSelected);

          this.changed = true;
        }
        break;
      case 'edit':
        if (this.categorySelected != '') {
          parentNode = this.flatNodeMap.get(this.nodeSelected);
          this.database.updateItem(parentNode, this.categorySelected);
          this.changed = true;
        }
        break;
      case 'add-icon':
        this.saveIcon();
        break;
      case 'new-cat':
        if (this.categorySelected != '') {
          this.database.insertRootItem({
            item: this.categorySelected,
            id: null,
            children: [],
            icon: '',
            level: 0,
            changed: true,
            owner: true,
          });
          this.changed = true;
        }
        break;
    }
    this.closeModal();
  }

  saveIcon() {
    if (this.uploader.queue.length > 0) {
      this.saving();

      this.uploader.setOptions(
        this._categoryService.getUrlIconOptions(this.nodeSelected.id)
      );

      this.uploader.uploadAll();
    }
  }

  closeModal() {
    this.openModal = false;
    setTimeout(() => {
      this.showModal = false;
    }, 200);
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  saveError() {
    Swal.fire('Ops!', 'Ocorreu um erro ao tentar salvar.', 'error');
  }

  saveComplete(msg = undefined) {
    this.changed = false;
    Swal.fire('Salvo!', msg ? msg : 'Os departamentos foram salvos', 'success');
  }

  saving() {
    Swal.fire({
      title: 'Salvando...',
      icon: 'info',
      showCancelButton: false,
      allowOutsideClick: false,
      showConfirmButton: false,
    });
  }

  checkTreeChanced(arr, idcategory, parent, treeSChanged) {
    for (let i in arr) {
      let subcategoria = arr[i];

      if (subcategoria.changed) {
        treeSChanged.push({
          id: subcategoria.id,
          item: subcategoria.item,
          idcategory: idcategory,
          parent_id: parent != null ? parent.id : null,
        });
      }

      if (subcategoria.children && subcategoria.children.length > 0) {
        this.checkTreeChanced(
          subcategoria.children,
          idcategory,
          subcategoria,
          treeSChanged
        );
      }
    }
  }

  onSubmit() {
    // console.log(this.dataSource.data);
    let treeSChanged = [];
    let treeCChanged = [];
    for (let i in this.dataSource.data) {
      let categoria = this.dataSource.data[i];
      // nova
      if (categoria.id == null) {
        treeCChanged.push(categoria);
      } else {
        if (categoria.changed) {
          treeCChanged.push({ id: categoria.id, item: categoria.item });
        }

        if (categoria.children && categoria.children.length > 0) {
          this.checkTreeChanced(
            categoria.children,
            categoria.id,
            null,
            treeSChanged
          );
        }
      }
    }

    if (
      treeSChanged.length > 0 ||
      treeCChanged.length > 0 ||
      this.categoryDeleted.length > 0
    ) {
      this.saving();

      this._categoryService
        .storeTree({
          categories: treeCChanged,
          subcategories: treeSChanged,
          deleted: this.categoryDeleted,
        })
        .subscribe((res) => {
          if (res['success']) {
            this.categoryDeleted = [];
            this.database.initialize(res['categorias']);
            this.saveComplete();
          } else {
            this.saveError();
          }
        });
    }
    return false;
  }
}
